import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, Mail } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import { useSiteData } from '../contexts/SiteDataContext';
import { getCollection } from '../data/site';

const CollectionHero = ({ collection }) => (
  <section className="relative h-[45vh] md:h-[55vh] overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${collection.image})` }}>
      <div className="absolute inset-0 bg-black/35" />
    </div>
    <div className="relative z-10 h-full flex items-end">
      <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-white/50 hover:text-white/80 transition-colors mb-4">
          <ArrowLeft size={13} /> All artwork
        </Link>
        <h1 className="text-2xl md:text-4xl font-light text-white tracking-[0.01em]">{collection.title}</h1>
      </div>
    </div>
  </section>
);

const CollectionDescription = ({ collection }) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-18">
    <FadeIn>
      <div className="max-w-2xl">
        <p className="text-[15px] md:text-[16px] leading-[1.85] text-[#555] font-light">{collection.description}</p>
        {collection.quote && (
          <blockquote className="mt-8 pl-5 border-l-2 border-[#e5e5e5]">
            <p className="text-[13px] leading-[1.75] text-[#999] italic">&ldquo;{collection.quote}&rdquo;</p>
          </blockquote>
        )}
      </div>
    </FadeIn>
  </section>
);

const PieceCard = ({ piece, onClick }) => (
  <div className="group cursor-pointer" onClick={onClick} data-testid={`piece-card-${piece.id}`}>
    <div className="relative overflow-hidden aspect-[4/3] bg-[#f5f5f5]">
      <img src={piece.image} alt={piece.title} className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
    </div>
    <div className="mt-3">
      <h3 className="text-[13px] text-[#444] group-hover:text-[#1a1a1a] transition-colors">{piece.title}</h3>
      <p className="text-[11px] text-[#aaa] mt-0.5">{piece.medium}{piece.year ? ` \u2014 ${piece.year}` : ''}</p>
      {piece.dimensions && <p className="text-[11px] text-[#ccc] mt-0.5">{piece.dimensions}</p>}
    </div>
  </div>
);

const Lightbox = ({ piece, artistEmail, onClose }) => {
  if (!piece) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-white/97 backdrop-blur-sm flex items-center justify-center p-4 md:p-12" onClick={onClose}>
      <button className="absolute top-6 right-6 text-[#ccc] hover:text-[#555] transition-colors z-[101]" onClick={onClose} data-testid="lightbox-close"><X size={22} /></button>
      <div className="max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
        <img src={piece.image} alt={piece.title} className="w-full h-full object-contain max-h-[70vh]" />
        <div className="mt-5 text-center">
          <p className="text-[14px] text-[#444]">{piece.title}</p>
          <p className="text-[12px] text-[#aaa] mt-1">{piece.medium}{piece.year ? ` \u2014 ${piece.year}` : ''}</p>
          {piece.dimensions && <p className="text-[11px] text-[#ccc] mt-0.5">{piece.dimensions}</p>}
          {piece.description && <p className="text-[12px] text-[#999] mt-3 max-w-md mx-auto italic leading-relaxed">{piece.description}</p>}
          <a href={`mailto:${artistEmail}?subject=Inquiry about "${piece.title}"`} className="inline-flex items-center gap-2 mt-5 text-[11px] text-[#888] hover:text-[#1a1a1a] transition-colors border border-[#ddd] hover:border-[#aaa] px-5 py-2">
            <Mail size={11} /> Inquire about this work
          </a>
        </div>
      </div>
    </div>
  );
};

const CollectionNavigation = ({ collections, currentIndex }) => {
  const prev = currentIndex > 0 ? collections[currentIndex - 1] : null;
  const next = currentIndex < collections.length - 1 ? collections[currentIndex + 1] : null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-16">
      <div className="border-t border-[#eee] pt-10 flex justify-between items-center">
        {prev ? (
          <Link to={`/artwork/${prev.id}`} className="inline-flex items-center gap-2 text-[12px] text-[#999] hover:text-[#1a1a1a] transition-colors">
            <ArrowLeft size={13} /> {prev.title}
          </Link>
        ) : <div />}
        {next ? (
          <Link to={`/artwork/${next.id}`} className="inline-flex items-center gap-2 text-[12px] text-[#999] hover:text-[#1a1a1a] transition-colors">
            {next.title} <ArrowRight size={13} />
          </Link>
        ) : <div />}
      </div>
    </section>
  );
};

const ArtworkDetail = () => {
  const { id } = useParams();
  const { collections, artistInfo } = useSiteData();
  const [selectedPiece, setSelectedPiece] = useState(null);

  const collection = getCollection(id);

  if (!collection) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#999] text-[13px]">Collection not found</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-5 text-[12px] text-[#999] hover:text-[#1a1a1a] transition-colors">
            <ArrowLeft size={13} /> Back to artwork
          </Link>
        </div>
      </div>
    );
  }

  const currentIndex = collections.findIndex((c) => c.id === id);

  return (
    <div data-testid={`artwork-detail-${collection.id}`}>
      <CollectionHero collection={collection} />
      <CollectionDescription collection={collection} />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8">
        <FadeIn>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[12px] text-[#bbb]">{collection.pieces.length} {collection.pieces.length === 1 ? 'work' : 'works'}</p>
              <div className="w-8 h-px bg-[#e5e5e5] mt-3" />
            </div>
            <a href={`mailto:${artistInfo?.email}?subject=Inquiry about ${collection.title} collection`} className="inline-flex items-center gap-2 text-[12px] text-[#888] hover:text-[#1a1a1a] transition-colors border border-[#ddd] hover:border-[#aaa] px-5 py-2.5">
              <Mail size={12} /> Inquire about this collection
            </a>
          </div>
        </FadeIn>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {collection.pieces.map((piece) => (
            <FadeIn key={piece.id}>
              <PieceCard piece={piece} onClick={() => setSelectedPiece(piece)} />
            </FadeIn>
          ))}
        </div>
      </section>

      <CollectionNavigation collections={collections} currentIndex={currentIndex} />
      <Lightbox piece={selectedPiece} artistEmail={artistInfo?.email} onClose={() => setSelectedPiece(null)} />
    </div>
  );
};

export default ArtworkDetail;
